import React, { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import {
  ArrowLeft,
  Heart,
  Shield,
  Clock,
  AlertCircle,
  Trophy,
  ChevronRight,
  Sparkles,
  RotateCcw
} from 'lucide-react'
import simulationsData from '../data/simulations.json'
import toast from 'react-hot-toast'
import Footer from '../components/Footer'
import '../styles/simulation.css'

/**
 * SimulationScreen Component
 * Interactive survival scenarios with decision trees
 */
const SimulationScreen = () => {
  const { simulationId } = useParams()
  const navigate = useNavigate()
  const [simulation, setSimulation] = useState(null)
  const [currentDecision, setCurrentDecision] = useState(null)
  const [playerState, setPlayerState] = useState({
    health: 100,
    safety: 50,
    timeRemaining: 15
  })
  const [decisionsHistory, setDecisionsHistory] = useState([])
  const [simulationComplete, setSimulationComplete] = useState(false)
  const [loading, setLoading] = useState(true)
  const [selectedOption, setSelectedOption] = useState(null)

  useEffect(() => {
    // Load simulation data
    const simData = simulationsData[simulationId]
    if (simData) {
      setSimulation(simData)
      setCurrentDecision(simData.scenario.decisions[0])
      setPlayerState({
        health: simData.scenario.initialHealth,
        safety: simData.scenario.initialSafety,
        timeRemaining: simData.scenario.timeLimit
      })
    }
    setLoading(false)
  }, [simulationId])

  const renderShell = (content, options = { showHeader: false }) => (
    <div className="simulation-shell">
      <div className="landing-ambient" />

      {options.showHeader && (
        <header className="simulation-header">
          <div className="container">
            <motion.button
              className="back-button"
              onClick={() => navigate('/simulations')}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
            >
              <ArrowLeft size={20} />
              <span>{options.backLabel || 'Exit Simulation'}</span>
            </motion.button>

            <motion.div
              className="simulation-info"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
            >
              <h1 className="simulation-title">{simulation?.title}</h1>
              <p className="simulation-description">{simulation?.description}</p>
            </motion.div>
          </div>
        </header>
      )}

      {options.statusBar && (
        <motion.div
          className="status-bar"
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <div className="container">
            <div className="status-indicators">
              <div className="status-item health">
                <Heart size={20} />
                <div className="status-bar-wrapper">
                  <motion.div
                    className="status-bar-fill"
                    animate={{ width: `${playerState.health}%` }}
                    transition={{ duration: 0.5 }}
                  />
                </div>
                <span>{playerState.health}%</span>
              </div>

              <div className="status-item safety">
                <Shield size={20} />
                <div className="status-bar-wrapper">
                  <motion.div
                    className="status-bar-fill"
                    animate={{ width: `${playerState.safety}%` }}
                    transition={{ duration: 0.5 }}
                  />
                </div>
                <span>{playerState.safety}%</span>
              </div>

              <div className="status-item time">
                <Clock size={20} />
                <span>{playerState.timeRemaining} min</span>
              </div>
            </div>
          </div>
        </motion.div>
      )}

      {content}

      <Footer />
    </div>
  )

  if (loading) {
    return renderShell(
      <div className="container">
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Loading simulation...</p>
        </div>
      </div>
    )
  }

  if (!simulation) {
    return renderShell(
      <div className="container">
        <div className="error-state">
          <AlertCircle size={48} />
          <h2>Simulation not found</h2>
          <button className="btn btn-primary" onClick={() => navigate('/')}>
            Back to Home
          </button>
        </div>
      </div>
    )
  }

  const handleOptionSelect = (option, index) => {
    if (selectedOption !== null) return
    
    setSelectedOption(index)
    
    // Apply outcome
    const newState = {
      health: Math.max(0, Math.min(100, playerState.health + option.outcome.health)),
      safety: Math.max(0, Math.min(100, playerState.safety + option.outcome.safety)),
      timeRemaining: Math.max(0, playerState.timeRemaining + option.outcome.time)
    }
    
    setPlayerState(newState)
    
    // Add to history
    setDecisionsHistory([
      ...decisionsHistory,
      {
        decision: currentDecision.text,
        choice: option.text,
        outcome: option.outcome.message
      }
    ])
    
    // Show feedback
    if (option.outcome.health < 0 || option.outcome.safety < -10) {
      toast.error(option.outcome.message)
    } else if (option.outcome.safety > 20) {
      toast.success(option.outcome.message)
    } else {
      toast(option.outcome.message)
    }
    
    // Move to next decision after delay
    setTimeout(() => {
      if (option.nextDecision === 'final-outcome' || newState.health <= 0 || newState.timeRemaining <= 0) {
        setSimulationComplete(true)
      } else {
        const nextDec = simulation.scenario.decisions.find(d => d.id === option.nextDecision)
        if (nextDec) {
          setCurrentDecision(nextDec)
          setSelectedOption(null)
        } else {
          setSimulationComplete(true)
        }
      }
    }, 2000)
  }

  const calculateScore = () => {
    const healthScore = playerState.health * 0.3
    const safetyScore = playerState.safety * 0.5
    const timeScore = (playerState.timeRemaining / simulation.scenario.timeLimit) * 20
    return Math.round(healthScore + safetyScore + timeScore)
  }

  const getPerformanceRating = (score) => {
    if (score >= 90) return { rating: 'Expert Survivor', color: 'var(--secondary)' }
    if (score >= 75) return { rating: 'Well Prepared', color: 'var(--primary)' }
    if (score >= 60) return { rating: 'Lucky Survivor', color: 'var(--warning)' }
    return { rating: 'Needs Practice', color: 'var(--danger)' }
  }

  if (simulationComplete) {
    const score = calculateScore()
    const performance = getPerformanceRating(score)

    return renderShell(
      <div className="container simulation-main">
        <motion.div
          className="simulation-complete"
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5 }}
        >
          <motion.div
            className="complete-icon"
            initial={{ scale: 0 }}
            animate={{ scale: 1, rotate: [0, -10, 10, 0] }}
            transition={{ delay: 0.2, duration: 0.5 }}
          >
            <Trophy size={64} />
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
          >
            Simulation Complete!
          </motion.h1>
          <motion.h2
            className="simulation-subtitle"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.35 }}
          >
            {simulation.title}
          </motion.h2>

          <motion.div
            className="final-stats"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
          >
            <div className="stat-card">
              <Heart size={24} className="text-red-500" />
              <span className="stat-value">{playerState.health}%</span>
              <span className="stat-label">Health</span>
            </div>
            <div className="stat-card">
              <Shield size={24} className="text-blue-500" />
              <span className="stat-value">{playerState.safety}%</span>
              <span className="stat-label">Safety</span>
            </div>
            <div className="stat-card">
              <Clock size={24} className="text-orange-500" />
              <span className="stat-value">{playerState.timeRemaining}m</span>
              <span className="stat-label">Time Left</span>
            </div>
          </motion.div>

          <motion.div
            className="performance-summary"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
          >
            <div className="score-display" style={{ borderColor: performance.color }}>
              <Sparkles size={24} style={{ color: performance.color }} />
              <span className="score-number" style={{ color: performance.color }}>{score}</span>
              <span className="score-label">Overall Score</span>
            </div>
            <p className="performance-rating" style={{ color: performance.color }}>
              {performance.rating}
            </p>
          </motion.div>

          <motion.div
            className="decision-summary"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
          >
            <h3>Your Decisions</h3>
            {decisionsHistory.map((item, index) => (
              <motion.div
                key={index}
                className="decision-item"
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.7 + index * 0.1 }}
              >
                <p className="decision-question">{item.decision}</p>
                <p className="decision-choice">→ {item.choice}</p>
                <p className="decision-outcome">{item.outcome}</p>
              </motion.div>
            ))}
          </motion.div>

          <motion.div
            className="simulation-actions"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8 }}
          >
            <button
              className="btn btn-secondary"
              onClick={() => window.location.reload()}
            >
              <RotateCcw size={18} />
              Try Again
            </button>
            <button
              className="btn btn-primary"
              onClick={() => navigate('/simulations')}
            >
              More Simulations
              <ChevronRight size={18} />
            </button>
          </motion.div>
        </motion.div>
      </div>
    )
  }

  return renderShell(
    <main className="container simulation-main">
      <motion.div
        className="decision-content"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
      >
        <AnimatePresence mode="wait">
          <motion.div
            key={currentDecision.id}
            className="decision-scenario"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.3 }}
          >
            <h2>{currentDecision.text}</h2>
            {currentDecision.timer > 0 && (
              <motion.p
                className="timer-warning"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.2 }}
              >
                <AlertCircle size={16} />
                Quick! You have {currentDecision.timer} seconds to decide!
              </motion.p>
            )}
          </motion.div>
        </AnimatePresence>

        <div className="decision-options">
          {currentDecision.options.map((option, index) => (
            <motion.div
              key={index}
              className={`option-card ${selectedOption === index ? 'selected' : ''} ${
                selectedOption !== null && selectedOption !== index ? 'not-selected' : ''
              }`}
              onClick={() => selectedOption === null && handleOptionSelect(option, index)}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 + index * 0.1 }}
              whileHover={selectedOption === null ? { scale: 1.01, y: -2 } : {}}
              whileTap={selectedOption === null ? { scale: 0.99 } : {}}
            >
              <span className="option-number">{index + 1}</span>
              <p className="option-text">{option.text}</p>
              {selectedOption === index && (
                <motion.div
                  className="option-feedback"
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                >
                  <ChevronRight size={20} />
                </motion.div>
              )}
            </motion.div>
          ))}
        </div>

        <AnimatePresence>
          {selectedOption !== null && (
            <motion.div
              className="outcome-message"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
            >
              <p>{currentDecision.options[selectedOption].outcome.message}</p>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </main>,
    { showHeader: true, statusBar: true }
  )
}

export default SimulationScreen
